import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  startOfMonth, 
  endOfMonth, 
  subMonths, 
  addMonths,
  setDate,
  isBefore,
  isAfter,
  format
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Account {
  id: string;
  name: string;
  type: string;
  balance: number;
  closing_day: number | null;
  due_day: number | null;
  credit_limit: number | null;
  color: string;
  institution: string | null;
}

interface Transaction {
  id: string;
  description: string;
  amount: number;
  transaction_date: string;
  type: 'income' | 'expense' | 'transfer';
  is_paid: boolean;
  category: {
    id: string;
    name: string;
    icon: string;
    color: string;
  } | null;
}

interface InvoicePeriod {
  startDate: Date;
  endDate: Date;
  dueDate: Date;
  label: string;
}

export function useCreditCardInvoice(creditCardId: string | null) {
  const [creditCard, setCreditCard] = useState<Account | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const [initialIndexSet, setInitialIndexSet] = useState(false);

  // Calculate invoice periods based on closing day and due day
  const invoicePeriods = useMemo<InvoicePeriod[]>(() => {
    const closingDay = creditCard?.closing_day || 1;
    const dueDay = creditCard?.due_day || Math.min(closingDay + 10, 28);
    const today = new Date();
    const periods: InvoicePeriod[] = [];

    // Generate invoice periods: 2 future + current + 5 past = 8 total
    // The reference is the DUE DATE month (vencimento)
    for (let i = -2; i < 6; i++) {
      // dueMonth is the month when the invoice is due (vencimento)
      const dueMonth = subMonths(today, i);
      const dueDate = setDate(dueMonth, dueDay);
      
      // Calculate the closing date based on relationship between closing and due days
      // If due day > closing day, closing is in the same month as due
      // If due day < closing day, closing is in the month before due
      let closingDate: Date;
      if (dueDay > closingDay) {
        closingDate = setDate(dueMonth, closingDay);
      } else {
        closingDate = setDate(subMonths(dueMonth, 1), closingDay);
      }
      
      // Period starts on previous month's closing day and ends on closing date
      // Previous closing is one month before current closing
      const previousClosingDate = subMonths(closingDate, 1);
      // Handle month length differences - ensure we get the correct day
      const previousClosingDay = Math.min(closingDay, endOfMonth(previousClosingDate).getDate());
      const periodStart = new Date(previousClosingDate.getFullYear(), previousClosingDate.getMonth(), previousClosingDay);
      const periodEnd = closingDate;

      periods.push({
        startDate: periodStart,
        endDate: periodEnd,
        dueDate: dueDate,
        label: format(dueDate, "MMMM 'de' yyyy", { locale: ptBR })
      });
    }

    return periods;
  }, [creditCard]);

  const currentPeriod = invoicePeriods[selectedPeriodIndex];

  // Set initial period to the invoice that is due next (or most recently due if today passed all due dates)
  useEffect(() => {
    if (invoicePeriods.length > 0 && !initialIndexSet) {
      const today = new Date();
      
      // Find the first invoice where due date is >= today (fatura atual ou próxima a vencer)
      // Periods are sorted from future to past, so we find the last one where due >= today
      let targetIndex = -1;
      for (let i = invoicePeriods.length - 1; i >= 0; i--) {
        if (!isBefore(invoicePeriods[i].dueDate, today)) {
          targetIndex = i;
          break;
        }
      }
      
      // If no future invoice found, use the most recent one (index 0 after reversing, or first past due)
      if (targetIndex === -1) {
        targetIndex = invoicePeriods.length - 1;
      }
      
      setSelectedPeriodIndex(targetIndex);
      setInitialIndexSet(true);
    }
  }, [invoicePeriods, initialIndexSet]);

  // Reset initial index when creditCardId changes
  useEffect(() => {
    if (creditCardId) {
      setInitialIndexSet(false);
      setSelectedPeriodIndex(0);
      setTransactions([]);
    }
  }, [creditCardId]);

  // Fetch credit card account details
  useEffect(() => {
    if (!creditCardId) {
      setCreditCard(null);
      return;
    }

    const fetchCreditCard = async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .eq('id', creditCardId)
        .eq('type', 'credit_card')
        .maybeSingle();

      if (!error && data) {
        setCreditCard(data as Account);
      }
    };

    fetchCreditCard();
  }, [creditCardId]);

  // Refetch function for external use
  const fetchTransactions = useCallback(async () => {
    if (!creditCardId || invoicePeriods.length === 0 || !initialIndexSet) {
      return;
    }

    const period = invoicePeriods[selectedPeriodIndex];
    if (!period) {
      setTransactions([]);
      return;
    }

    setLoading(true);
    try {
      // Calculate the invoice date for this period (first day of due month)
      const invoiceDate = format(period.dueDate, 'yyyy-MM-01');
      
      // Single RPC replaces 4 separate queries
      const { data, error } = await supabase.rpc("get_credit_card_invoice", {
        p_account_id: creditCardId,
        p_invoice_date: invoiceDate,
        p_period_start: format(period.startDate, 'yyyy-MM-dd'),
        p_period_end: format(period.endDate, 'yyyy-MM-dd'),
      });

      if (error) throw error;

      const result = data as any;
      const allTransactions = (result?.transactions || []).map((t: any) => ({
        ...t,
        amount: Number(t.amount),
        category: t.category && !Array.isArray(t.category) ? t.category : (Array.isArray(t.category) ? t.category[0] : null),
      }));

      setTransactions(allTransactions);
    } finally {
      setLoading(false);
    }
  }, [creditCardId, selectedPeriodIndex, invoicePeriods, initialIndexSet]);

  // Fetch transactions for the selected period
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Calculate invoice totals
  const invoiceSummary = useMemo(() => {
    const expenses = transactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + Number(t.amount), 0);
    
    const payments = transactions
      .filter(t => t.type === 'income' || t.type === 'transfer')
      .reduce((sum, t) => sum + Number(t.amount), 0);

    return {
      totalExpenses: expenses,
      totalPayments: payments,
      invoiceTotal: expenses - payments,
      transactionCount: transactions.length
    };
  }, [transactions]);

  // Check if invoice is due (past due date and has balance)
  const isOverdue = useMemo(() => {
    if (!currentPeriod) return false;
    return isBefore(currentPeriod.dueDate, new Date()) && invoiceSummary.invoiceTotal > 0;
  }, [currentPeriod, invoiceSummary]);

  // Check if current period is the active (current) invoice
  const isCurrentInvoice = useMemo(() => {
    if (!currentPeriod) return false;
    const today = new Date();
    return isBefore(currentPeriod.startDate, today) && isAfter(currentPeriod.endDate, today);
  }, [currentPeriod]);

  return {
    creditCard,
    transactions,
    loading,
    invoicePeriods,
    currentPeriod,
    selectedPeriodIndex,
    setSelectedPeriodIndex,
    invoiceSummary,
    isOverdue,
    isCurrentInvoice,
    refetch: fetchTransactions
  };
}
